/*
 * <before-after-slider> — draggable before/after image comparison.
 * Shared by the homepage (D1) and product page (D2). Pointer (mouse + touch +
 * pen) and keyboard driven; rAF-throttled; no external dependencies.
 * Reads the initial divider position from the data-start attribute and updates
 * the --ba-position custom property (0–100) consumed by component-before-after.css.
 */
(function () {
  if (window.customElements.get('before-after-slider')) return;

  class BeforeAfterSlider extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('[data-ba-frame]');
      this.handle = this.querySelector('[data-ba-handle]');
      if (!this.frame || !this.handle) return;

      this.beforeLabel = this.querySelector('.before-after__label--before');
      this.afterLabel = this.querySelector('.before-after__label--after');
      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize);

      this.position = this.clamp(parseFloat(this.getAttribute('data-start')));
      if (isNaN(this.position)) this.position = 50;
      this.dragging = false;
      this.rafId = null;
      this.lastX = null;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      // Press anywhere on the frame jumps the divider there and starts dragging;
      // pressing the handle just drags.
      this.frame.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('pointermove', this.onPointerMove);
      this.addEventListener('pointerup', this.onPointerUp);
      this.addEventListener('pointercancel', this.onPointerUp);
      this.handle.addEventListener('keydown', this.onKeyDown);

      this.apply();
    }

    disconnectedCallback() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      window.removeEventListener('resize', this.onResize);
    }

    onResize() {
      this.updateLabels();
    }

    clamp(value) {
      return Math.max(0, Math.min(100, value));
    }

    apply() {
      this.style.setProperty('--ba-position', this.position);
      this.handle.setAttribute('aria-valuenow', Math.round(this.position));
      this.updateLabels();
    }

    /* A label only shows while its own image still covers it: "Before" hides
       once the divider moves left of the label's right edge, "After" hides
       once the divider moves right of the label's left edge. */
    updateLabels() {
      if (!this.beforeLabel && !this.afterLabel) return;
      const width = this.frame.getBoundingClientRect().width;
      if (!width) return;
      const divider = (this.position / 100) * width;
      if (this.beforeLabel) {
        const edge = this.beforeLabel.offsetLeft + this.beforeLabel.offsetWidth;
        this.beforeLabel.classList.toggle('before-after__label--hidden', divider < edge);
      }
      if (this.afterLabel) {
        this.afterLabel.classList.toggle('before-after__label--hidden', divider > this.afterLabel.offsetLeft);
      }
    }

    setPosition(value) {
      this.position = this.clamp(value);
      this.apply();
    }

    pctFromX(clientX) {
      const rect = this.frame.getBoundingClientRect();
      if (!rect.width) return this.position;
      return ((clientX - rect.left) / rect.width) * 100;
    }

    onPointerDown(event) {
      this.dragging = true;
      // Capture on the host so we keep receiving moves outside the frame.
      try {
        this.setPointerCapture(event.pointerId);
      } catch (e) {}
      if (!this.handle.contains(event.target)) {
        this.setPosition(this.pctFromX(event.clientX));
      }
      this.handle.focus({ preventScroll: true });
      event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      this.lastX = event.clientX;
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.setPosition(this.pctFromX(this.lastX));
      });
    }

    onPointerUp(event) {
      this.dragging = false;
      try {
        this.releasePointerCapture(event.pointerId);
      } catch (e) {}
    }

    onKeyDown(event) {
      let next = this.position;
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next -= 1;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          next += 1;
          break;
        case 'PageDown':
          next -= 10;
          break;
        case 'PageUp':
          next += 10;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = 100;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.setPosition(next);
    }
  }

  window.customElements.define('before-after-slider', BeforeAfterSlider);
})();
